import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  StellarAction,
  StellarActionResponse,
  StellarOfflineSummary,
  StellarState,
} from "../shared/stellar-types";
import { applyStellarAction, simulateStellar } from "../stellar-engine";
import { ApiRequestError } from "../api/client";
import { bootstrapStellar, deleteStellarProgress, submitStellarAction } from "../api/stellar";
import { useAuth } from "../app/AuthContext";

const PULSE_FLUSH_MS = 750;
const SYNC_INTERVAL_MS = 25_000;
const RETRY_DELAY_MS = 2_000;

interface QueueEntry {
  action: StellarAction;
  key: string;
  pulseCount: number;
  critical: boolean;
  resolve?: (success: boolean) => void;
}

interface StellarContextValue {
  state: StellarState | null;
  offline: StellarOfflineSummary | null;
  stateVersion: number;
  status: "idle" | "loading" | "ready" | "error";
  syncing: boolean;
  busy: boolean;
  queuedPulses: number;
  pulseEcho: number;
  error: string | null;
  notice: string[];
  refresh: () => Promise<void>;
  resetProgress: () => Promise<boolean>;
  pulse: () => boolean;
  perform: (action: Exclude<StellarAction, { type: "PULSE" }>) => Promise<boolean>;
  flushPending: () => void;
  dismissOffline: () => void;
  clearFeedback: () => void;
}

const StellarContext = createContext<StellarContextValue | null>(null);

export function StellarProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [serverState, setServerState] = useState<StellarState | null>(null);
  const [offline, setOffline] = useState<StellarOfflineSummary | null>(null);
  const [stateVersion, setStateVersion] = useState(-1);
  const [status, setStatus] = useState<StellarContextValue["status"]>("idle");
  const [syncing, setSyncing] = useState(false);
  const [pendingCritical, setPendingCritical] = useState(0);
  const [queuedPulses, setQueuedPulses] = useState(0);
  const [pulseEcho, setPulseEcho] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string[]>([]);
  const [clockSyncedAt, setClockSyncedAt] = useState(monotonicNow);
  const [liveTick, setLiveTick] = useState(0);

  const serverStateRef = useRef<StellarState | null>(null);
  const displayStateRef = useRef<StellarState | null>(null);
  const stateVersionRef = useRef(-1);
  const requestEpoch = useRef(0);
  const ownerRef = useRef<string | null>(null);
  const bootstrapKeyRef = useRef<string | null>(null);
  const queueRef = useRef<QueueEntry[]>([]);
  const inFlightRef = useRef(false);
  const resettingRef = useRef(false);
  const pulseBufferRef = useRef(0);
  const outstandingPulsesRef = useRef(0);
  const pulseTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const drainRef = useRef<() => void>(() => undefined);
  const flushRef = useRef<() => void>(() => undefined);

  const clearTimers = useCallback(() => {
    if (pulseTimerRef.current !== null) window.clearTimeout(pulseTimerRef.current);
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    pulseTimerRef.current = null;
    retryTimerRef.current = null;
  }, []);

  const clearClientState = useCallback(() => {
    requestEpoch.current += 1;
    clearTimers();
    for (const entry of queueRef.current) entry.resolve?.(false);
    queueRef.current = [];
    inFlightRef.current = false;
    pulseBufferRef.current = 0;
    outstandingPulsesRef.current = 0;
    serverStateRef.current = null;
    stateVersionRef.current = -1;
    bootstrapKeyRef.current = null;
    ownerRef.current = null;
    setServerState(null);
    setOffline(null);
    setStateVersion(-1);
    setQueuedPulses(0);
    setPendingCritical(0);
    setSyncing(false);
    setError(null);
    setNotice([]);
    setStatus("idle");
  }, [clearTimers]);

  const acceptState = useCallback((response: StellarActionResponse, messages = response.messages) => {
    if (response.stateVersion <= stateVersionRef.current) return false;
    stateVersionRef.current = response.stateVersion;
    serverStateRef.current = response.state;
    setStateVersion(response.stateVersion);
    setServerState(response.state);
    setClockSyncedAt(monotonicNow());
    setStatus("ready");
    if (messages.length) setNotice(messages);
    return true;
  }, []);

  const settlePulse = useCallback((count: number) => {
    if (!count) return;
    outstandingPulsesRef.current = Math.max(0, outstandingPulsesRef.current - count);
    setQueuedPulses(outstandingPulsesRef.current);
  }, []);

  const drainQueue = useCallback(async () => {
    if (inFlightRef.current || !auth.csrfToken) return;
    inFlightRef.current = true;
    setSyncing(true);
    let retryLater = false;
    try {
      while (queueRef.current.length) {
        const entry = queueRef.current[0]!;
        try {
          const response = await submitStellarAction(entry.action, auth.csrfToken, entry.key);
          queueRef.current.shift();
          settlePulse(entry.pulseCount);
          acceptState(response);
          setError(null);
          if (entry.critical) setPendingCritical((value) => Math.max(0, value - 1));
          entry.resolve?.(true);
        } catch (cause) {
          if (cause instanceof ApiRequestError && (cause.status === 401 || cause.code === "AUTH_REQUIRED")) {
            clearClientState();
            auth.invalidate();
            return;
          }
          const uncertain = cause instanceof ApiRequestError
            && (cause.code === "NETWORK_ERROR" || cause.status >= 500);
          setError(cause instanceof Error ? cause.message : "星核操作未能写入。 ");
          if (uncertain) {
            retryLater = true;
            if (retryTimerRef.current === null) {
              retryTimerRef.current = window.setTimeout(() => {
                retryTimerRef.current = null;
                drainRef.current();
              }, RETRY_DELAY_MS);
            }
            break;
          }
          queueRef.current.shift();
          settlePulse(entry.pulseCount);
          if (entry.critical) setPendingCritical((value) => Math.max(0, value - 1));
          entry.resolve?.(false);
        }
      }
    } finally {
      inFlightRef.current = false;
      setSyncing(false);
      if (!retryLater && queueRef.current.length) queueMicrotask(() => drainRef.current());
    }
  }, [acceptState, auth.csrfToken, auth.invalidate, clearClientState, settlePulse]);

  drainRef.current = () => { void drainQueue(); };

  const enqueue = useCallback((entry: QueueEntry) => {
    if (resettingRef.current) {
      entry.resolve?.(false);
      return;
    }
    queueRef.current.push(entry);
    drainRef.current();
  }, []);

  const flushPending = useCallback(() => {
    if (pulseTimerRef.current !== null) window.clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = null;
    let count = pulseBufferRef.current;
    pulseBufferRef.current = 0;
    while (count > 0) {
      const chunk = Math.min(25, count);
      enqueue({
        action: { type: "PULSE", count: chunk },
        key: crypto.randomUUID(),
        pulseCount: chunk,
        critical: false,
      });
      count -= chunk;
    }
  }, [enqueue]);

  flushRef.current = flushPending;

  const refresh = useCallback(async () => {
    if (!auth.user || !auth.csrfToken) {
      clearClientState();
      return;
    }
    const epoch = ++requestEpoch.current;
    const owner = auth.user.id;
    ownerRef.current = owner;
    if (!serverStateRef.current) setStatus("loading");
    setError(null);
    const key = bootstrapKeyRef.current ?? crypto.randomUUID();
    bootstrapKeyRef.current = key;
    try {
      const response = await bootstrapStellar(auth.csrfToken, key);
      if (epoch !== requestEpoch.current || ownerRef.current !== owner) return;
      bootstrapKeyRef.current = null;
      if (response.stateVersion > stateVersionRef.current) {
        stateVersionRef.current = response.stateVersion;
        serverStateRef.current = response.state;
        setStateVersion(response.stateVersion);
        setServerState(response.state);
        setOffline(response.offline);
        setClockSyncedAt(monotonicNow());
      }
      setStatus("ready");
    } catch (cause) {
      if (epoch !== requestEpoch.current) return;
      if (cause instanceof ApiRequestError && (cause.status === 401 || cause.code === "AUTH_REQUIRED")) {
        clearClientState();
        auth.invalidate();
        return;
      }
      setError(cause instanceof Error ? cause.message : "无法载入星核进度。 ");
      if (!serverStateRef.current) setStatus("error");
    }
  }, [auth.csrfToken, auth.invalidate, auth.user, clearClientState]);

  useEffect(() => {
    const owner = auth.user?.id ?? null;
    if (!owner || !auth.csrfToken) {
      if (ownerRef.current) clearClientState();
      return;
    }
    if (ownerRef.current !== owner || !serverStateRef.current) void refresh();
  }, [auth.csrfToken, auth.user?.id, clearClientState, refresh]);

  useEffect(() => {
    if (!serverState) return;
    const timer = window.setInterval(() => setLiveTick((value) => value + 1), 1_000);
    return () => window.clearInterval(timer);
  }, [serverState]);

  useEffect(() => {
    if (status !== "ready") return;
    const timer = window.setInterval(() => {
      flushRef.current();
      if (!queueRef.current.some((entry) => entry.action.type === "SYNC")) {
        enqueue({ action: { type: "SYNC" }, key: crypto.randomUUID(), pulseCount: 0, critical: false });
      }
    }, SYNC_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enqueue, status]);

  useEffect(() => {
    const flush = () => flushRef.current();
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const state = useMemo(() => {
    if (!serverState) return null;
    const elapsedSeconds = Math.max(0, monotonicNow() - clockSyncedAt) / 1_000;
    const live = simulateStellar(serverState, elapsedSeconds).state;
    return replayPulses(live, queuedPulses);
  }, [clockSyncedAt, liveTick, queuedPulses, serverState]);
  displayStateRef.current = state;

  const pulse = useCallback(() => {
    if (resettingRef.current) return false;
    const current = displayStateRef.current;
    if (!current) return false;
    try {
      applyStellarAction(current, { type: "PULSE", count: 1 }, { nowMs: Date.now(), id: optimisticId });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "当前无法推进演化。 ");
      return false;
    }
    pulseBufferRef.current += 1;
    outstandingPulsesRef.current += 1;
    setQueuedPulses(outstandingPulsesRef.current);
    setPulseEcho((value) => value + 1);
    setError(null);
    setNotice([]);
    if (pulseTimerRef.current !== null) window.clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = window.setTimeout(() => flushRef.current(), PULSE_FLUSH_MS);
    return true;
  }, []);

  const perform = useCallback((action: Exclude<StellarAction, { type: "PULSE" }>) => {
    if (resettingRef.current) return Promise.resolve(false);
    flushPending();
    setError(null);
    setNotice([]);
    setPendingCritical((value) => value + 1);
    return new Promise<boolean>((resolve) => enqueue({
      action,
      key: crypto.randomUUID(),
      pulseCount: 0,
      critical: true,
      resolve,
    }));
  }, [enqueue, flushPending]);

  const resetProgress = useCallback(async () => {
    if (!auth.user || !auth.csrfToken || resettingRef.current) return false;

    const queueBarrier = perform({ type: "SYNC" });
    resettingRef.current = true;
    try {
      if (!(await queueBarrier)) return false;
      await deleteStellarProgress(auth.csrfToken);
      clearClientState();
      setStatus("loading");
      await refresh();
      const restarted = serverStateRef.current !== null;
      if (restarted) setNotice(["星核纪元进度已重置。"]);
      return restarted;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法重置星核进度。 ");
      return false;
    } finally {
      resettingRef.current = false;
    }
  }, [auth.csrfToken, auth.user, clearClientState, perform, refresh]);

  const value = useMemo<StellarContextValue>(() => ({
    state,
    offline,
    stateVersion,
    status,
    syncing,
    busy: pendingCritical > 0,
    queuedPulses,
    pulseEcho,
    error,
    notice,
    refresh,
    resetProgress,
    pulse,
    perform,
    flushPending,
    dismissOffline: () => setOffline(null),
    clearFeedback: () => { setError(null); setNotice([]); },
  }), [
    state, offline, stateVersion, status, syncing, pendingCritical, queuedPulses,
    pulseEcho, error, notice, refresh, resetProgress, pulse, perform, flushPending,
  ]);

  return <StellarContext.Provider value={value}>{children}</StellarContext.Provider>;
}

export function useStellar() {
  const value = useContext(StellarContext);
  if (!value) throw new Error("useStellar must be used inside StellarProvider");
  return value;
}

function replayPulses(state: StellarState, count: number): StellarState {
  let next = state;
  let remaining = count;
  while (remaining > 0) {
    const chunk = Math.min(25, remaining);
    try {
      next = applyStellarAction(next, { type: "PULSE", count: chunk }, {
        nowMs: Date.now(),
        id: optimisticId,
      }).state;
    } catch {
      break;
    }
    remaining -= chunk;
  }
  return next;
}

function optimisticId() {
  return "optimistic";
}

function monotonicNow() {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}
